// Cuánta leche cruda queda por vender, y el cuadre del día.
//
// ── El caso ──
//
// «Hay una leche que no entra a la quesera: el hermano de Gabriel, Juan Pablo,
// se lleva unos litros y esa leche debe quedar registrada con fecha y litros, y
// debe descontarse de la que entra a la quesera.»
//
// Juan Pablo la paga, así que es una VENTA de leche cruda — no un retiro. Va
// por la misma puerta que el queso: su cartera, su remisión, sus ganancias. Una
// segunda forma de vender terminaría dando cifras distintas de la primera.
//
// ── El cuadre, que es lo que de verdad sirve ──
//
//     recibida  −  la que entró a las tinas  −  la vendida  =  lo que queda
//
// Cuando esa resta no da, hay leche que no está donde dice el papel. Y eso se
// tiene que saber EL MISMO DÍA: al cierre del mes ya no hay a quién
// preguntarle. El 18 de agosto quedaron 6.396 litros cuajados contra 1.421
// comprados, y nadie lo vio hasta que alguien fue a mirar.

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

export const VACA = 'vaca';
export const BUFALA = 'bufala';

const esBufala = (t) => String(t || '').toLowerCase() === BUFALA;

// Los litros recibidos ese día, separados por leche.
export function recibidoDelDia(recibos) {
  const r = { [VACA]: 0, [BUFALA]: 0 };
  for (const x of (Array.isArray(recibos) ? recibos : [])) {
    r[esBufala(x.tipo_leche) ? BUFALA : VACA] += Math.max(0, num(x.litros));
  }
  return r;
}

// Los litros que entraron a las tinas ese día.
//
// De `litros_vaca` y `litros_bufala`, no de `litros`: la tina se revuelve, y
// una cuajada de 600 litros puede llevar 400 de vaca y 200 de búfala. Contarla
// entera como vaca dejaría la búfala pareciendo sin gastar.
export function cuajadoDelDia(cuajadas) {
  const r = { [VACA]: 0, [BUFALA]: 0 };
  for (const c of (Array.isArray(cuajadas) ? cuajadas : [])) {
    const lb = Math.max(0, num(c.litros_bufala));
    const lv = c.litros_vaca !== undefined && c.litros_vaca !== null
      ? Math.max(0, num(c.litros_vaca))
      : Math.max(0, num(c.litros) - lb);
    r[VACA] += lv;
    r[BUFALA] += lb;
  }
  return r;
}

// Los litros de leche cruda vendidos ese día.
//
// `lineas` son renglones de venta con su producto. Solo cuentan los productos
// marcados como leche: el resto es queso, y el queso ya salió de una tina.
export function vendidoDelDia(lineas) {
  const r = { [VACA]: 0, [BUFALA]: 0 };
  for (const l of (Array.isArray(lineas) ? lineas : [])) {
    if (!l.es_leche) continue;
    r[esBufala(l.es_leche) ? BUFALA : VACA] += Math.max(0, num(l.kilos));
  }
  return r;
}

// Lo que queda por vender de cada leche, y el cuadre.
export function disponible({ recibos, cuajadas, lineas } = {}) {
  const rec = recibidoDelDia(recibos);
  const cua = cuajadoDelDia(cuajadas);
  const ven = vendidoDelDia(lineas);

  const uno = (t) => {
    const queda = Math.round((rec[t] - cua[t] - ven[t]) * 10) / 10;
    return {
      recibido: Math.round(rec[t] * 10) / 10,
      cuajado: Math.round(cua[t] * 10) / 10,
      vendido: Math.round(ven[t] * 10) / 10,
      queda,
      // NEGATIVO quiere decir que salió más leche de la que entró. No se
      // esconde ni se recorta a cero: es exactamente el aviso que hace falta,
      // y taparlo dejaría el descuadre creciendo en silencio.
      falta: queda < -0.05 ? Math.abs(queda) : 0,
    };
  };
  return { [VACA]: uno(VACA), [BUFALA]: uno(BUFALA) };
}

// A cuánto le costó el litro ese día, por leche.
//
// Promedio ponderado: la plata que se pagó entre los litros que entraron. No es
// el precio de una finca ni el más alto — es lo que de verdad costó la leche
// que hay en el tanque.
//
// La leche cruda se vende a precio variable, y lo único que evita venderla por
// debajo de lo que costó es tener este número al lado de la casilla.
export function costoDelDia(recibos) {
  const acc = { [VACA]: { plata: 0, litros: 0 }, [BUFALA]: { plata: 0, litros: 0 } };
  for (const x of (Array.isArray(recibos) ? recibos : [])) {
    const t = esBufala(x.tipo_leche) ? BUFALA : VACA;
    const L = Math.max(0, num(x.litros));
    acc[t].litros += L;
    acc[t].plata += L * Math.max(0, num(x.precio_litro));
  }
  const uno = (t) => acc[t].litros > 0
    ? Math.round(acc[t].plata / acc[t].litros)
    : null;   // Sin recibos NO se inventa un costo: null se puede callar.
  return { [VACA]: uno(VACA), [BUFALA]: uno(BUFALA) };
}

// Cuánto se puede vender de esta leche ahora mismo. Nunca negativo: no se
// puede vender lo que no hay, aunque las cuentas del día estén descuadradas.
export function sePuedeVender(disp, tipo) {
  const d = disp && disp[esBufala(tipo) ? BUFALA : VACA];
  return d ? Math.max(0, d.queda) : 0;
}

// La frase del cuadre, para la pantalla. Vale más un renglón que se entiende
// que una tabla de cuatro cifras que nadie resta.
export function frase(d) {
  if (!d) return '';
  if (d.falta > 0) {
    return `Salieron ${d.falta} L más de los que entraron. Revise los recibos del día.`;
  }
  if (d.recibido === 0) return 'Todavía no se ha recibido leche hoy.';
  if (d.queda === 0) return 'Toda la leche del día está cuajada o vendida.';
  return `Quedan ${d.queda} L sin cuajar ni vender.`;
}
